import React, { useEffect, useState } from "react";
import {
  Link,
  useParams,
  useSearchParams,
  useLocation,
} from "react-router-dom";

// Formik Validation
import * as Yup from "yup";
import { useFormik } from "formik";

import withRouter from "../../components/Common/withRouter";

// API
import { post } from "../../api/axios";
import endpoints from "../../api/endpoints";

// Utils
import { extractErrorMessage } from "../../utils/errorHandler";

// Toast notifications
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// reusable components
import CustomInput from "../../components/AuthenticationComponents/CustomInput";
import CustomButton from "../../components/AuthenticationComponents/CustomButton";
import logo from "../../assets/images/authentication/logo-auth.svg";
import "../../assets/scss/custom/pages/Authentication.scss";
import zapIcon from "../../assets/images/authentication/zap.svg";
import creditCardIcon from "../../assets/images/authentication/credit-card.svg";
import giftIcon from "../../assets/images/authentication/gift.svg";
import arrowRightIcon from "../../assets/images/authentication/arrow-right.svg";

const ResetPassword = (props) => {
  document.title = "Reset Password | Omaha Food Lovers";
  const location = useLocation();

  // Determine which portal based on route
  const isRestaurant =
    location.pathname === "/restaurant/reset-password/:token";
  const isAdmin = location.pathname.startsWith("/admin/reset-password");

  // Configure toastr options
  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Get token from URL params and email from query params
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      password: "",
      password_confirmation: "",
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .trim()
        .min(8, "Password must be at least 8 characters")
        .max(128, "Password cannot exceed 128 characters")
        .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
        .matches(/[a-z]/, "Password must contain at least one lowercase letter")
        .matches(/[0-9]/, "Password must contain at least one number")
        .matches(
          /[^A-Za-z0-9]/,
          "Password must contain at least one special character",
        )
        .required("Please enter your password"),
      password_confirmation: Yup.string()
        .trim()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values) => {
      if (!token || !email) {
        setError("Invalid reset link. Please request a new password reset.");
        toastr.error(
          "Invalid reset link. Please request a new password reset.",
        );
        return;
      }

      setLoading(true);
      setError("");
      setSuccess("");
      try {
        // Use appropriate endpoint based on portal
        let endpoint;
        if (isAdmin) {
          endpoint = endpoints.adminResetPassword;
        } else if (isRestaurant) {
          endpoint = endpoints.restaurantResetPassword;
        } else {
          endpoint = endpoints.resetPassword;
        }

        const response = await post(endpoint, {
          token: token,
          email: email,
          password: values.password,
          password_confirmation: values.password_confirmation,
        });

        if (response.data.success || response.data.status) {
          setSuccess(response.data.message || "Password reset successfully!");
          toastr.success(
            response.data.message || "Password reset successfully!",
          );

          // Navigate to appropriate login page after successful reset
          setTimeout(() => {
            if (isAdmin) {
              props.router.navigate("/admin/login");
            } else if (isRestaurant) {
              props.router.navigate("/restaurant/login");
            } else {
              props.router.navigate("/login");
            }
          }, 2000);
        } else {
          setError(response.data.message || "Failed to reset password");
          toastr.error(response.data.message || "Failed to reset password");
        }
      } catch (error) {
        const errorMessage = extractErrorMessage(error);
        setError(errorMessage);
        toastr.error(errorMessage);
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    document.body.className = "authentication-bg";
    return function cleanup() {
      document.body.className = "";
    };
  }, []);

  return (
    <React.Fragment>
      <div className="auth-page">
        <div className="auth-page-grid">
          {/* left brand / marketing panel */}
          <div className="auth-brand-panel">
            <div className="auth-brand-panel-inner">
              <Link to="/" className="auth-logo">
                <img
                  className="auth-logo-icon"
                  src={logo}
                  alt="Omaha Food Lovers"
                />
              </Link>

              <h1 className="auth-headline">
                {isAdmin ? (
                  <>
                    Admin Portal
                    <br />
                    <span className="auth-headline-accent">
                      Omaha Food Lovers
                    </span>
                  </>
                ) : isRestaurant ? (
                  <>
                    Restaurant Portal
                    <br />
                    <span className="auth-headline-accent">
                      Omaha Food Lovers
                    </span>
                  </>
                ) : (
                  <>
                    Earn rewards every time
                    <br />
                    you dine in{" "}
                    <span className="auth-headline-accent">Omaha</span>
                  </>
                )}
              </h1>

              <p className="auth-subline">
                {isAdmin ? (
                  <>
                    Manage the platform.
                    <br />
                    Oversee members, restaurants, and platform analytics.
                  </>
                ) : isRestaurant ? (
                  <>
                    Manage your restaurant account.
                    <br />
                    Track redemptions, view platform fees, and more.
                  </>
                ) : (
                  <>
                    Get 5% back at 200+ restaurants.
                    <br />
                    Track your dining streak. Unlock exclusive perks.
                  </>
                )}
              </p>

              <ul className="auth-feature-list">
                {isAdmin ? (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Manage member accounts and restaurant listings.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        View platform analytics and settlement reports.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        Configure platform settings and manage permissions.
                      </span>
                    </li>
                  </>
                ) : isRestaurant ? (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Track voucher redemptions and customer activity.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>Manage your restaurant profile and settings.</span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        Access platform analytics and settlement reports.
                      </span>
                    </li>
                  </>
                ) : (
                  <>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={creditCardIcon} alt="Credit Card" />
                      </span>
                      <span>
                        Link your card once. Earn 5% back automatically at 200+
                        local restaurants.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={giftIcon} alt="Gift" />
                      </span>
                      <span>
                        Redeem points for restaurant vouchers. Your rewards stay
                        local, supporting Omaha's dining scene.
                      </span>
                    </li>
                    <li>
                      <span className="auth-feature-icon" aria-hidden="true">
                        <img src={zapIcon} alt="Zap" />
                      </span>
                      <span>
                        100% free. No subscription, no hidden fees. Just eat and
                        earn.
                      </span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          </div>

          {/* right form panel */}
          <div className="auth-form-panel">
            <div className="auth-form-card">
              <div className="auth-form-header">
                <h2>Set New Password</h2>
                <p>Must be at least 8 characters.</p>
                {!token || !email ? (
                  <p className="auth-server-error">
                    Invalid or expired reset link.
                  </p>
                ) : null}
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  validation.handleSubmit();
                  return false;
                }}
              >
                <div className="custom-input-row">
                  <CustomInput
                    label="New Password"
                    name="password"
                    type="password"
                    placeholder="••••••••"
                    value={validation.values.password}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password &&
                      !!validation.errors.password
                    }
                    errorText={validation.errors.password}
                    autoComplete="new-password"
                    className="glass"
                  />
                  <CustomInput
                    label="Confirm Password"
                    name="password_confirmation"
                    type="password"
                    placeholder="••••••••"
                    value={validation.values.password_confirmation}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    invalid={
                      validation.touched.password_confirmation &&
                      !!validation.errors.password_confirmation
                    }
                    errorText={validation.errors.password_confirmation}
                    autoComplete="new-password"
                    className="glass"
                  />
                </div>

                {error ? <p className="auth-server-error">{error}</p> : null}

                {success ? (
                  <p className="auth-server-success">{success}</p>
                ) : null}

                <div className="auth-submit-wrap">
                  <CustomButton
                    type="submit"
                    variant="primary"
                    iconPosition="right"
                    icon={arrowRightIcon}
                    loading={loading}
                  >
                    Reset Password
                  </CustomButton>
                </div>

                <p className="auth-login-link">
                  Back to <Link to="/login">Log In</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
        <p className="auth-brand-footer">
          © Omaha Food Lovers {new Date().getFullYear()} - All Rights Reserved.
        </p>
      </div>
    </React.Fragment>
  );
};

export default withRouter(ResetPassword);
